import { z } from "zod";

// ============================================================================
// A2P 10DLC REGISTRATION — ENUMS
// ============================================================================

/**
 * Twilio-defined business types for A2P brand registration.
 * Must match exactly what Twilio's Trust Hub API accepts.
 */
export const A2P_BUSINESS_TYPES = [
  "Co-operative",
  "Corporation",
  "Limited Liability Corporation",
  "Non-profit Corporation",
  "Partnership"
] as const;

/**
 * Twilio-defined business industries for A2P brand registration.
 * Values must be UPPERCASE — Twilio API rejects lowercase.
 */
export const A2P_BUSINESS_INDUSTRIES = [
  "AUTOMOTIVE",
  "AGRICULTURE",
  "BANKING",
  "CONSUMER",
  "EDUCATION",
  "ENGINEERING",
  "ENERGY",
  "OIL_AND_GAS",
  "FAST_FOOD",
  "FINANCIAL",
  "FINTECH",
  "FOOD_AND_BEVERAGE",
  "GOVERNMENT",
  "HEALTHCARE",
  "HOSPITALITY",
  "INSURANCE",
  "LEGAL",
  "MANUFACTURING",
  "MEDIA",
  "ONLINE",
  "RAW_MATERIALS",
  "REAL_ESTATE",
  "RELIGION",
  "RETAIL",
  "JEWELRY",
  "TECHNOLOGY",
  "TELECOMMUNICATIONS",
  "TRANSPORTATION",
  "TRAVEL",
  "ELECTRONICS",
  "NOT_FOR_PROFIT"
] as const;

export const A2P_COMPANY_TYPES = ["private", "public"] as const;

/**
 * Business registration identifier types accepted by Twilio.
 * EIN is the most common for US businesses.
 */
export const A2P_REGISTRATION_IDENTIFIERS = [
  "EIN",
  "DUNS",
  "CBN",
  "CN",
  "ACN",
  "CIN",
  "VAT",
  "VATRN",
  "RN",
  "Other"
] as const;

// ============================================================================
// A2P 10DLC REGISTRATION — FORM DATA
// ============================================================================

/**
 * Authorized representative — required by Twilio for Trust Hub.
 * This person is the primary contact for compliance-related communications.
 */
export const A2pAuthorizedRepSchema = z.object({
  firstName: z.string().min(1),
  lastName: z.string().min(1),
  email: z.string().email(),
  phone: z.string().min(5),
  businessTitle: z.string().min(1),
  jobPosition: z.string().min(1)
});
export type A2pAuthorizedRep = z.infer<typeof A2pAuthorizedRepSchema>;

/**
 * Business address for Trust Hub registration.
 */
export const A2pBusinessAddressSchema = z.object({
  street: z.string().min(1),
  city: z.string().min(1),
  region: z.string().min(1),
  postalCode: z.string().min(1),
  isoCountry: z.string().length(2).default("US")
});
export type A2pBusinessAddress = z.infer<typeof A2pBusinessAddressSchema>;

/**
 * Full A2P 10DLC registration form data.
 *
 * All fields are static and known at build time — unlike the regulatory
 * compliance bundle which has dynamic fields per country/type.
 */
export const A2pFormSchema = z.object({
  // Business identity
  businessName: z.string().min(1),
  ein: z.string().min(1),
  registrationIdentifier: z.enum(A2P_REGISTRATION_IDENTIFIERS).default("EIN"),
  businessType: z.enum(A2P_BUSINESS_TYPES),
  businessIndustry: z.enum(A2P_BUSINESS_INDUSTRIES),
  companyType: z.enum(A2P_COMPANY_TYPES),

  // Online presence
  websiteUrl: z.string().url(),
  socialMediaUrl: z.string().url().optional(),

  // Contact
  brandContactEmail: z.string().email(),
  businessRegionsOfOperation: z.string().default("USA"),

  // Authorized representative
  authorizedRep: A2pAuthorizedRepSchema,

  // Address
  address: A2pBusinessAddressSchema
});
export type A2pFormData = z.infer<typeof A2pFormSchema>;

/**
 * Per-step validation schemas for the A2P wizard.
 *
 * Step 0: Business identity (name, EIN, type, industry)
 * Step 1: Contact + online presence (website, email, authorized rep)
 * Step 2: Review & submit
 */
export const A2pStepSchemas = {
  businessIdentity: z.object({
    businessName: A2pFormSchema.shape.businessName,
    ein: A2pFormSchema.shape.ein,
    registrationIdentifier: A2pFormSchema.shape.registrationIdentifier,
    businessType: A2pFormSchema.shape.businessType,
    businessIndustry: A2pFormSchema.shape.businessIndustry,
    companyType: A2pFormSchema.shape.companyType
  }),
  contactAndPresence: z.object({
    websiteUrl: A2pFormSchema.shape.websiteUrl,
    socialMediaUrl: A2pFormSchema.shape.socialMediaUrl,
    brandContactEmail: A2pFormSchema.shape.brandContactEmail,
    authorizedRep: A2pFormSchema.shape.authorizedRep,
    address: A2pFormSchema.shape.address
  })
} as const;
